
import {
  ControlElement,
  JsonFormsRendererRegistryEntry,
} from "@jsonforms/core";
import { computed, defineComponent, nextTick, onMounted, ref } from "vue";
import { rendererProps, useJsonFormsControl } from "@jsonforms/vue";
import { useVanillaControl } from "@jsonforms/vue-vanilla";
import ControlWrapper from "@/core/json-forms/controls/ControlWrapper.vue";
import StringControlRenderer from "@/core/json-forms/controls/StringControlRenderer.vue";
import SelectionSetTree from "@/core/json-forms/controls/selection-set/SelectionSetTree.vue";
import {
  hydrate,
  serialize,
} from "@/core/json-forms/controls/selection-set/selectionModel";
import { fetchDefaultSelection } from "@/flow/application/graphqlSchemaApi";
import RendererConfigurations from "@/core/json-forms/renderer-configurations";

const controlRenderer = defineComponent({
  name: RendererConfigurations.SelectionSetControlRenderer.name,
  components: {
    ControlWrapper,
    StringControlRenderer,
    SelectionSetTree,
  },
  // Custom props threaded from EditStep's <JsonForms> so the raw StringControlRenderer keeps its
  // full function/output-injection behavior; anything undeclared is forwarded via $attrs.
  inheritAttrs: false,
  props: {
    stepIdx: { type: Number, required: false, default: undefined },
    outputList: { type: Array, required: false, default: () => [] },
    userDefinedVariables: { type: Array, required: false, default: () => [] },
    // EditStep passes systemVariables as a name→tooltip map, not an array; accept both so the raw
    // StringControlRenderer fallback doesn't trip a prop-type warning.
    systemVariables: {
      type: [Array, Object],
      required: false,
      default: () => [],
    },
    expressions: { type: Array, required: false, default: () => [] },
    metaoutputList: { type: Array, required: false, default: () => [] },
    showOtherInputs: { type: Boolean, required: false, default: true },
    ...rendererProps<ControlElement>(),
  },
  setup(props: any) {
    const vanilla = useVanillaControl(useJsonFormsControl(props));
    const { control } = vanilla;
    // Depending on the jsonforms binding, `control` may be a ref or an already-unwrapped reactive
    // object. Resolve the underlying control node for both shapes so setup access can't throw.
    const node = computed(() => {
      const c = control as any;
      return c && c.value !== undefined ? c.value : c;
    });
    const mode = ref<"tree" | "raw">("tree");

    // String-field values in this app are stored as a token array
    // (e.g. [{"name":"{ id name }","type":"input"}]) by StringControlRenderer, and the runtime
    // reconstructs the text by concatenating the input-token names. Decode to the plain GraphQL
    // string for the tree, and re-encode on write so persistence/runtime stay identical to raw.
    function decodeSelection(raw: any): string {
      if (typeof raw !== "string" || raw.length === 0) {
        return typeof raw === "string" ? raw : "";
      }
      const trimmed = raw.trim();
      if (trimmed.startsWith("[")) {
        try {
          const tokens = JSON.parse(trimmed);
          if (Array.isArray(tokens)) {
            return tokens
              .filter((t) => !t || !t.type || t.type === "input")
              .map((t) => (t && typeof t.name === "string" ? t.name : ""))
              .join("");
          }
        } catch (e) {
          // not a token array; fall through to raw
        }
      }
      return raw;
    }

    function encodeSelection(value: string): string {
      return JSON.stringify([{ name: value, type: "input" }]);
    }

    // A token array can carry non-input tokens (output/metaoutput/variable/function references)
    // injected via Raw mode. The tree only round-trips the plain GraphQL text, so encodeSelection
    // would collapse the whole value to a single input token and silently drop those references.
    // Detect that case so the tree can be shown read-only instead of destroying data on the first
    // edit. Un-tokenized plain strings are never affected.
    function hasNonInputTokens(raw: any): boolean {
      if (typeof raw !== "string") return false;
      const trimmed = raw.trim();
      if (!trimmed.startsWith("[")) return false;
      try {
        const tokens = JSON.parse(trimmed);
        if (Array.isArray(tokens)) {
          return tokens.some((t) => t && t.type && t.type !== "input");
        }
      } catch (e) {
        // not a token array; treat as plain string
      }
      return false;
    }

    const options = computed(() => node.value?.schema?.options || {});
    const modelString = computed(() => decodeSelection(node.value?.data));
    // The tree can't safely edit a value that carries non-input tokens (see hasNonInputTokens).
    const treeReadOnly = computed(() => hasNonInputTokens(node.value?.data));
    const hasContext = computed(
      () =>
        !!options.value.connectorId &&
        options.value.revision != null &&
        !!options.value.rootType
    );
    // Reset-to-default additionally needs the action coordinates for the default-selection endpoint.
    const canReset = computed(
      () =>
        hasContext.value && !!options.value.operation && !!options.value.field
    );

    // Set when the tree can't parse the current selection (invalid GraphQL, arguments, drift): the
    // component drops to raw mode so the value is never lost, and offers recovery actions below.
    const parseError = ref(false);
    // The value the step opened with — used to revert an unparseable edit back to the last-loaded
    // (typically last-saved) selection without touching the backend.
    const initialData = ref<any>(undefined);
    const hasInitial = computed(() => initialData.value != null);
    onMounted(() => {
      initialData.value = node.value?.data;
    });

    function onUpdate(value: string) {
      // A user edit means the selection is valid again as far as the tree is concerned.
      parseError.value = false;
      vanilla.handleChange(node.value.path, encodeSelection(value));
    }

    function switchMode(next: "tree" | "raw") {
      // State crosses the toggle through control.data: it is already the serialized string, and
      // re-entering tree mode re-parses it on mount (design section 7.5). Retrying the tree clears
      // the previous parse error; if it still can't parse, @fallback sets it again.
      if (next === "tree") parseError.value = false;
      mode.value = next;
    }

    function onFallback() {
      parseError.value = true;
      mode.value = "raw";
    }

    async function enterTreeWith(value: string) {
      vanilla.handleChange(node.value.path, value);
      parseError.value = false;
      // Let the data write propagate before the tree remounts and hydrates from control.data.
      await nextTick();
      mode.value = "tree";
    }

    // Recovery: rebuild the generated default selection client-side (same hydrate→serialize path the
    // tree uses) and re-enter tree mode with a guaranteed-parseable value.
    async function resetToDefault() {
      if (!canReset.value) return;
      const def = await fetchDefaultSelection(
        options.value.connectorId,
        options.value.revision,
        options.value.operation,
        options.value.field
      );
      if (def) {
        await enterTreeWith(encodeSelection(serialize(hydrate(def))));
      }
    }

    // Recovery: restore the selection the step was opened with (discards the unparseable edit).
    async function revertToSaved() {
      if (!hasInitial.value) return;
      await enterTreeWith(initialData.value);
    }

    return {
      ...vanilla,
      mode,
      options,
      hasContext,
      canReset,
      parseError,
      hasInitial,
      modelString,
      treeReadOnly,
      onUpdate,
      switchMode,
      onFallback,
      resetToDefault,
      revertToSaved,
      RendererConfigurations,
    };
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: RendererConfigurations.SelectionSetControlRenderer.tester,
};
